import { AuthorEntity, Course, CourseEntity, LearnEntity } from '@/graphql/generated/graphql';
import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';

type LearnToStart = { idModule: number | string; idLesson: number | string };

export function getCourseCategoriesByLearn(courses: CourseEntity[] | null) {
  if (!courses || courses?.length === 0) {
    return {};
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const categories = courses?.reduce<{ [key: string]: any }>((acc, curr) => {
    const category = curr?.attributes?.course_category?.data?.attributes;

    if (!category) {
      return {};
    }

    if (acc[`${category.name}`]) {
      return acc;
    }

    return {
      ...acc,
      [`${category.name}`]: {
        name: category.name,
        description: category.description,
      },
    };
  }, {}) as { [key: string]: { name?: string; description?: string } };

  return categories;
}

export function getStartLesson(course: Course, learn?: LearnEntity | null) {
  const lessons = course?.modules?.reduce((acc, curr) => {
    const moduleLessons = curr?.lessons?.map((lesson) => ({
      idLesson: lesson?.id || '',
      idModule: curr?.id || '',
    })) as LearnToStart[];

    return acc.concat(moduleLessons);
  }, [] as LearnToStart[]);

  if ((!learn?.id && (lessons || []).length > 0) || learn?.attributes?.completed) {
    return { idModule: lessons![0]?.idModule, idLesson: lessons![0]?.idLesson } as LearnToStart;
  }

  const learnItems =
    learn?.attributes?.learn_items?.data?.map((learnItem) => {
      return { idModule: learnItem.attributes?.idModule, idLesson: learnItem.attributes?.idLesson } as LearnToStart;
    }) || [];

  const lessonsToStudy = differenceWith(lessons, learnItems, isEqual);

  return lessonsToStudy.length >= 1 ? lessonsToStudy[0] : null;
}

export function getAuthors(course?: Course): Array<AuthorEntity> | null {
  if (!course) {
    return null;
  }

  let authors = [] as Array<AuthorEntity>;

  course?.modules?.forEach((m) => {
    m?.lessons?.forEach((lesson) => {
      authors = authors.concat(lesson!.authors!.data!);
    });
  });

  return uniqBy(authors, 'attributes.name') as Array<AuthorEntity>;
}

export const getCurrentLearn = (
  course?: Course | null,
  idModule?: number | string | null,
  idLesson?: number | string | null,
) => {
  let nextLesson = null;
  let nextModule = null;
  let previousLessonidModule = idModule;
  let previousIdLesson = null;
  let idNextLessonModule = idModule;

  if (!course || !idModule || !idLesson) {
    return { currentModule: null, currentLesson: null, nextLesson: null };
  }

  const currentModule = course?.modules?.find((m) => m?.id === idModule);

  if (!currentModule || currentModule?.lessons?.length === 0) {
    return { currentModule: null, currentLesson: null, nextLesson: null };
  }

  const currentModuleIndex = course?.modules?.findIndex((m) => m?.id === idModule);
  const currentLesson = currentModule?.lessons?.find((l) => l?.id === idLesson);

  if (!currentLesson) {
    return { currentModule, currentLesson: null, nextLesson: null };
  }

  const currentLessonIndex = currentModule?.lessons?.findIndex((l) => l?.id === idLesson);

  if (currentModuleIndex && currentModuleIndex > 0) {
    const previousModule = (course?.modules || [])[currentModuleIndex - 1];
    const previousModuleLessons = previousModule?.lessons || [];

    if (currentLessonIndex === 0 && previousModuleLessons.length > 0) {
      previousLessonidModule = previousModule?.id;
      previousIdLesson = previousModuleLessons[previousModuleLessons.length - 1]?.id;
    } else if (currentLessonIndex === 0 && previousModuleLessons.length === 0) {
      previousLessonidModule = null;
      previousIdLesson = null;
    }
  }

  if (typeof currentLessonIndex === 'number' && currentLessonIndex > 0) {
    previousLessonidModule = currentModule.id;
    previousIdLesson = (currentModule.lessons || [])[currentLessonIndex - 1]?.id;
  }

  if (typeof currentLessonIndex === 'number' && (currentModule.lessons || []).length - 1 > currentLessonIndex) {
    nextLesson = (currentModule.lessons || [])[currentLessonIndex + 1];
  }

  if (
    typeof currentModuleIndex === 'number' &&
    (currentModule.lessons || []).length - 1 === currentLessonIndex &&
    (course?.modules || []).length - 1 > currentModuleIndex
  ) {
    nextModule = (course?.modules || [])[currentModuleIndex + 1];
    nextLesson = (nextModule?.lessons || [])[0];
    idNextLessonModule = nextModule?.id;
  }

  return { currentModule, currentLesson, nextLesson, idNextLessonModule, previousLessonidModule, previousIdLesson };
};
